import React, { useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  OverlayTrigger,
  InputGroup,
  Container,
  Tooltip,
  Button,
  Form,
  Card,
  Col,
  Row
} from 'react-bootstrap';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { Helmet } from 'react-helmet-async';

import { useLocalization } from '../../contexts/LocalizationContext';
import { useNotifications } from '../../hooks/useNotifications';
import { useAuth } from '../../contexts/AuthContext';
import { getBaseRoute } from '../../utility';

import ainiLogoAndText from '../../../public/assets/img/Eduplan_Aini_logo_iconAndText_orangeAndBlack.svg';
import eduixLogo from '../../../public//assets/img/eduix_logo_200x82_orange.png';

import styles from './styles.module.scss';

const LocalLogin = () => {
  const { login, isLoggedIn, user, role, loading } = useAuth();
  const { locales, setLocale, localize } = useLocalization();
  const notifications = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();

  const [showPassword, setShowPassword] = useState(false);
  const emailRef = useRef();
  const passwordRef = useRef();

  const loginHandle = async (e) => {
    e.preventDefault();

    const data = {
      email: emailRef.current.value,
      password: passwordRef.current.value
    };
    try {
      const response = await login(data);

      if (response?.id) {
        let r;
        if (response?.roles) {
          if (response?.roles?.some((role) => role.level === 0)) {
            r = 0;
          } else {
            r = response.tenants[
              response.tenants.findIndex(
                (element) => element.id === response?.selectedTenant
              )
            ]?.roles?.find((role) => role.level).key;
          }
        }

        notifications.clear();
        notifications.notify('common.welcome');
        navigate(location.state?.from || getBaseRoute(response, r) || '/', {
          replace: true
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  if (isLoggedIn) {
    return (
      <Navigate
        to={location.state?.from || getBaseRoute(user, role) || '/'}
        replace
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${localize('login.pageHeader')} | ${localize(
          'common.appName'
        )}`}</title>
      </Helmet>

      <Container className={styles.loginPage}>
        <Row>
          <Col
            sm={{ span: 12 }}
            md={{ span: 8, offset: 2 }}
            lg={{ span: 6, offset: 3 }}
            xl={{ span: 6, offset: 3 }}>
            <Card className={`${styles.card} my-3`}>
              <Card.Header
                className='p-4'
                style={{
                  border: 0,
                  backgroundColor: 'white',
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}>
                <img
                  style={{ width: '15rem', height: 'auto' }}
                  aria-hidden='true'
                  src={ainiLogoAndText}
                  alt='Aini logo and name'
                />
                <h1 className='d-flex text-center m-0 flex-wrap'>
                  {localize('login.pageHeader')}
                </h1>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={(event) => loginHandle(event)}>
                  <Form.Group className='mb-3' controlId='email'>
                    <Form.Label>{localize('common.email')}</Form.Label>

                    <Form.Control
                      ref={emailRef}
                      className={styles.emailField}
                      type='email'
                      placeholder=''
                      required
                      disabled={loading}
                    />
                  </Form.Group>

                  <Form.Group className='mb-3' controlId='password'>
                    <Form.Label>{localize('common.password')}</Form.Label>
                    <InputGroup>
                      <Form.Control
                        ref={passwordRef}
                        className={styles.passwordField}
                        type={showPassword ? 'text' : 'password'}
                        placeholder=''
                        required
                        disabled={loading}
                      />
                      <OverlayTrigger
                        overlay={
                          <Tooltip id={`tooltip-top`}>
                            {!showPassword
                              ? localize('login.showPassword')
                              : localize('login.hidePassword')}
                          </Tooltip>
                        }>
                        <Button
                          variant='outline-secondary'
                          onClick={(e) => setShowPassword(!showPassword)}>
                          {!showPassword ? <Visibility /> : <VisibilityOff />}
                        </Button>
                      </OverlayTrigger>
                    </InputGroup>
                  </Form.Group>
                  <div className='d-grid gap-2'>
                    <Button
                      variant='primary'
                      type='submit'
                      size='lg'
                      style={{ textTransform: 'uppercase' }}
                      disabled={loading}>
                      {localize('login.btn.login')}
                    </Button>
                  </div>
                </Form>
                <hr />
                <div className='d-flex m-0 my-0 justify-content-center'>
                  {window._env?.urls?.appDetails && (
                    <a
                      className='btn btn-link'
                      target='_blank'
                      href={window._env.urls.appDetails}>
                      {localize('login.link.appDetails')}
                    </a>
                  )}
                  {window._env?.urls?.eduix && (
                    <a
                      className='btn btn-link'
                      target='_blank'
                      href={window._env.urls.eduix}
                      aria-label='Eduix'>
                      <img
                        style={{ maxHeight: '30px', marginTop: '-10px' }}
                        src={eduixLogo}
                        alt='Eduix'
                      />
                    </a>
                  )}
                </div>
              </Card.Body>
              {locales.length > 0 && (
                <Card.Footer className={styles.cardFooter}>
                  {locales.map((locale) => (
                    <Button
                      key={locale}
                      onClick={() => setLocale(locale)}
                      variant='link'>
                      {localize(`common.btn.locale.${locale}`)}
                    </Button>
                  ))}
                </Card.Footer>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default LocalLogin;
